import {
  Component,
  HostBinding,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { DomSanitizer, SafeStyle } from '@angular/platform-browser'
import { ComponentShape } from '@wm-front/shared/enums'

@Component({
  selector: 'wm-front-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class AvatarComponent {
  /** The username to display in the bottom left corner, in minimalist mode we only display the first letter */
  @Input() public username: string = 'Anonymous'

  /**  Specifies a title to be shown under the **name** */
  @Input() public title: string

  /**  Color of the area shown when no picture specified */
  @Input() public color: string

  /** The shape of the avatar */
  @Input() public shape: ComponentShape | `${ComponentShape}`

  /** The shape of the avatar */
  @Input() public size?: { height: string; width: string }

  @Input() public backgroundColor?: string

  /** The style of the background image to display */
  public imageBackgroundStyle: SafeStyle | null

  private domSanitizer = inject(DomSanitizer)

  /** Whether to show a user name or not */
  private _showName: boolean = true

  /**  Whether to show a user title or not */
  private _showTitle: boolean = true

  private _showInitials: boolean = true

  /** Whether or not the component should have the class 'square' */
  @HostBinding('class.square')
  public get rectangle(): boolean {
    return this.shape === ComponentShape.SQUARE
  }

  /** Whether or not the component should have the class 'semi-round' */
  @HostBinding('class.semi-round')
  public get semiRound(): boolean {
    return this.shape === ComponentShape.SEMI_ROUND
  }

  /** Whether or not the component should have the class 'round' */
  @HostBinding('class.round')
  public get round(): boolean {
    return this.shape === ComponentShape.ROUND
  }

  @Input()
  public get showName(): boolean {
    return this._showName
  }

  @Input()
  public get showTitle(): boolean {
    return this._showTitle
  }

  /**  Whether to show a user initials (if no picture specified) or not */
  @Input()
  public get showInitials(): boolean {
    return this._showInitials
  }

  /** Whether to show only a picture or also show the name and title */
  @Input()
  public get pictureOnly(): boolean {
    return !this.showName && !this.showTitle
  }

  /** Returns the first name of the username */
  public get firstNameFirstLetter(): string {
    return this.username?.charAt(0).toUpperCase() || ''
  }

  /** Returns the initials of the current logged in user i.e GR for Gaultier ROMON */
  public get initials(): string {
    if (this.username) {
      const names = this.username.split(' ')

      return names
        .map((n) => n.charAt(0))
        .splice(0, 2)
        .join('')
        .toUpperCase()
    }
    return ''
  }

  /**
   * Absolute path to a user picture or base64 image.
   * User name initials will be shown if no picture specified (GR for Gaultier ROMON).
   */
  @Input()
  public set picture(value: string) {
    this.imageBackgroundStyle = value
      ? this.domSanitizer.bypassSecurityTrustStyle(`url(${value})`)
      : null
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set showName(val: boolean) {
    this._showName = this.convertToBoolProperty(val)
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set showInitials(val: boolean) {
    this._showInitials = this.convertToBoolProperty(val)
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set showTitle(val: boolean) {
    this._showTitle = this.convertToBoolProperty(val)
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  public set pictureOnly(val: boolean) {
    this.showName = this.showTitle = !this.convertToBoolProperty(val)
  }

  private convertToBoolProperty(val: unknown): boolean {
    if (typeof val === 'string') {
      val = val.toLowerCase().trim()

      return val === 'true' || val === ''
    }

    return !!val
  }
}
